import { useState, useEffect, useRef } from 'react';
import { apiClient } from '../store/auth.store';
import toast from 'react-hot-toast';

interface MediaItem {
  id: string;
  url: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  mediaType: 'image' | 'sound';
  createdAt: string;
}

interface Props {
  mediaType: 'image' | 'sound' | 'all';
  onSelectUrl: (url: string, type: 'image' | 'sound') => void;
}

export default function MediaLibrary({ mediaType, onSelectUrl }: Props) {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'image' | 'sound'>(mediaType as any);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchMedia();
  }, [filter]);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/media/${filter}`);
      setMedia(data.media || []);
    } catch {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = file.type.startsWith('audio/') ? '/media/sounds' : '/media/images';
      const { data } = await apiClient.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File uploaded!');
      fetchMedia();
      if (onSelectUrl) onSelectUrl(data.url, data.mediaType);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(`/media/${id}`);
      toast.success('File deleted');
      fetchMedia();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleCopyUrl = (url: string) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copied!');
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredMedia = media.filter(
    (m) => filter === 'all' || m.mediaType === filter
  );

  return (
    <div className="media-library">
      <div className="media-header">
        <h2>Media Library</h2>
        <div className="media-actions">
          <div className="media-filter">
            <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All</button>
            <button className={`filter-btn ${filter === 'image' ? 'active' : ''}`} onClick={() => setFilter('image')}>Images</button>
            <button className={`filter-btn ${filter === 'sound' ? 'active' : ''}`} onClick={() => setFilter('sound')}>Sounds</button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,audio/*"
            onChange={handleUpload}
            style={{ display: 'none' }}
          />
          <button className="btn-upload" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? 'Uploading...' : '+ Upload'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="media-loading">Loading...</div>
      ) : filteredMedia.length === 0 ? (
        <div className="media-empty">
          <div className="empty-icon">📂</div>
          <p>No media files yet</p>
          <p className="helper-text">Upload images or sounds to use in your widget</p>
        </div>
      ) : (
        <div className={`media-grid ${filter === 'sound' ? 'sound-grid' : ''}`}>
          {filteredMedia.map((item) => (
            <div key={item.id} className="media-card">
              {item.mediaType === 'image' ? (
                <div className="media-preview">
                  <img src={item.url} alt={item.originalName} />
                </div>
              ) : (
                <div className="media-preview sound-preview">
                  <span className="sound-icon">🎵</span>
                  <span className="sound-name">{item.originalName}</span>
                  <button
                    className="btn-play"
                    onClick={(e) => {
                      e.stopPropagation();
                      const fullUrl = `${window.location.origin}${item.url}`;
                      const a = new Audio(fullUrl);
                      a.play().catch(() => toast.error('Cannot play sound'));
                    }}
                  >
                    ▶ Play
                  </button>
                </div>
              )}
              <div className="media-info">
                <span className="media-name" title={item.originalName}>{item.originalName}</span>
                <span className="media-size">{formatSize(item.size)}</span>
              </div>
              <div className="media-card-actions">
                <button className="btn-use" onClick={(e) => {
                      e.stopPropagation();
                      const full = `${window.location.origin}${item.url}`;
                      onSelectUrl(full, item.mediaType);
                      toast.success(`Selected: ${item.originalName}`);
                    }}>
                  Use
                </button>
                <button className="btn-copy" onClick={() => handleCopyUrl(item.url)}>
                  Copy
                </button>
                <button className="btn-del" onClick={() => handleDelete(item.id)}>
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .media-library { }
  .media-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
  }
  .media-header h2 {
    font-size: 18px;
    color: #e4e4e7;
  }
  .media-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .media-filter {
    display: flex;
    gap: 4px;
    background: #18181b;
    border-radius: 8px;
    padding: 4px;
  }
  .filter-btn {
    padding: 6px 14px;
    background: transparent;
    border: none;
    border-radius: 6px;
    color: #71717a;
    font-size: 13px;
    cursor: pointer;
  }
  .filter-btn.active { background: #27272a; color: #e4e4e7; }
  .btn-upload {
    padding: 8px 16px;
    background: #f59e0b;
    border: none;
    border-radius: 8px;
    color: white;
    font-weight: 600;
    font-size: 13px;
  }
  .btn-upload:hover:not(:disabled) { background: #d97706; }
  .btn-upload:disabled { opacity: 0.6; cursor: not-allowed; }
  
  .media-loading { text-align: center; padding: 40px; color: #71717a; }
  .media-empty { text-align: center; padding: 60px 20px; }
  .media-empty .empty-icon { font-size: 48px; margin-bottom: 12px; }
  .media-empty p { color: #71717a; }
  .media-empty .helper-text { font-size: 12px; color: #52525b; }
  
  .media-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
  }
  .media-card {
    background: #18181b;
    border: 1px solid #27272a;
    border-radius: 10px;
    overflow: hidden;
    transition: border-color 0.2s;
  }
  .media-card:hover { border-color: #3f3f46; }
  .media-preview {
    width: 100%;
    aspect-ratio: 1;
    background: #0f0f12;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .media-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .sound-preview {
    flex-direction: column;
    gap: 8px;
    padding: 20px;
  }
  .sound-icon { font-size: 32px; }
  .sound-name { font-size: 12px; color: #a1a1aa; text-align: center; word-break: break-all; }
  .btn-play {
    padding: 6px 16px;
    background: #27272a;
    border: none;
    border-radius: 6px;
    color: #e4e4e7;
    font-size: 12px;
    cursor: pointer;
  }
  .btn-play:hover { background: #3f3f46; }
  
  .media-info {
    padding: 10px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .media-name {
    font-size: 12px;
    color: #a1a1aa;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100px;
  }
  .media-size {
    font-size: 11px;
    color: #52525b;
  }
  .media-card-actions {
    display: flex;
    border-top: 1px solid #27272a;
  }
  .media-card-actions button {
    flex: 1;
    padding: 8px;
    background: transparent;
    border: none;
    color: #71717a;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .media-card-actions button:hover { background: #27272a; color: #e4e4e7; }
  .btn-use { color: #f59e0b; }
  .btn-del:hover { color: #ef4444; }
`;
